/** Small SVG building blocks shared by all renderers. */
import { escapeXml } from './escape';
import { FONT_STACKS, GraphOptions } from '../options';

export const round = (value: number) => Math.round(value * 100) / 100;

/** `rrggbb` -> `#rrggbb`; `transparent` -> `none`. */
export const paint = (color: string) => (color === 'transparent' ? 'none' : `#${color}`);

/** Rough text width estimate; good enough for placing and spacing labels. */
export const estimateTextWidth = (text: string, fontSize: number) => text.length * fontSize * 0.6;

export const cardBackground = (width: number, height: number, options: GraphOptions): string => {
    const stroke = options.showBorder ? options.borderWidth : 0;
    const inset = stroke / 2;
    return (
        `<rect x="${inset}" y="${inset}" width="${round(width - stroke)}" height="${round(height - stroke)}"` +
        ` rx="${options.borderRadius}" fill="${paint(options.colors.background)}"` +
        (stroke ? ` stroke="${paint(options.colors.border)}" stroke-width="${stroke}"` : '') +
        '/>'
    );
};

/** Wraps rendered content in the root <svg> with accessible title/description and the card. */
export const svgDocument = (args: {
    width: number;
    height: number;
    options: GraphOptions;
    title: string;
    description: string;
    body: string;
    defs?: string;
}): string =>
    `<svg xmlns="http://www.w3.org/2000/svg" width="${args.width}" height="${args.height}"` +
    ` viewBox="0 0 ${args.width} ${args.height}" role="img" aria-labelledby="gag-title gag-desc"` +
    ` font-family="${escapeXml(FONT_STACKS[args.options.font])}">` +
    `<title id="gag-title">${escapeXml(args.title)}</title>` +
    `<desc id="gag-desc">${escapeXml(args.description)}</desc>` +
    (args.defs ? `<defs>${args.defs}</defs>` : '') +
    cardBackground(args.width, args.height, args.options) +
    args.body +
    '</svg>';

export const LINE_GRADIENT_ID = 'gag-line-stroke';

/**
 * Stroke attributes for a series line, honoring `lineOpacity` and `lineGradient` (a left-to-right
 * fade). The gradient uses user-space coordinates so it still renders on a perfectly flat line.
 */
export const lineStroke = (
    options: GraphOptions,
    from: number,
    to: number,
): { attrs: string; defs: string } => {
    const color = paint(options.colors.line);
    const opacity = round(options.lineOpacity / 100);
    if (!options.lineGradient)
        return {
            attrs: `stroke="${color}"${opacity < 1 ? ` stroke-opacity="${opacity}"` : ''}`,
            defs: '',
        };
    return {
        attrs: `stroke="url(#${LINE_GRADIENT_ID})"`,
        defs:
            `<linearGradient id="${LINE_GRADIENT_ID}" gradientUnits="userSpaceOnUse" x1="${round(from)}" y1="0" x2="${round(to)}" y2="0">` +
            `<stop offset="0" stop-color="${color}" stop-opacity="${round(opacity * 0.2)}"/>` +
            `<stop offset="1" stop-color="${color}" stop-opacity="${opacity}"/>` +
            '</linearGradient>',
    };
};
